import React, { useState, useEffect, useCallback } from 'react';
import { Card, Table, Button, Badge, Modal, LoadingSkeleton } from './ui';
import { PlusIcon } from '@heroicons/react/24/outline';
import { safeErrorMessage } from '../utils/apiError';
import { hasAnyRole } from '../utils/roles';

const achievementTypes = ['tournament_winner', 'top_scorer', 'most_valuable', 'best_defender', 'sportsmanship', 'record_breaker'];
const levels = ['school', 'district', 'provincial', 'national'];
const RECORD_ROLES = ['administrator', 'principal', 'coach'];

function Sports({ token, user }) {
  const canRecord = hasAnyRole(user, RECORD_ROLES);
  const [sports, setSports] = useState([]);
  const [matches, setMatches] = useState([]);
  const [students, setStudents] = useState([]);
  const [leaderboard, setLeaderboard] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showMatchForm, setShowMatchForm] = useState(false);
  const [showAchievementForm, setShowAchievementForm] = useState(false);
  const [error, setError] = useState('');
  const [matchData, setMatchData] = useState({
    sport_id: '', opponent: '', match_date: '', venue: '', our_score: '', opponent_score: '',
    result: '', match_type: 'friendly', season: '', notes: ''
  });
  const [achievementData, setAchievementData] = useState({
    student_id: '', sport_id: '', achievement_type: '', title: '', description: '', date_awarded: '', level: 'school'
  });

  const fetchData = useCallback(async () => {
    try {
      const [sportsRes, matchesRes, studentsRes] = await Promise.all([
        fetch('/api/v1/sports', { headers: { 'Authorization': `Bearer ${token}` } }),
        fetch('/api/v1/sports-matches', { headers: { 'Authorization': `Bearer ${token}` } }),
        fetch('/api/v1/students', { headers: { 'Authorization': `Bearer ${token}` } })
      ]);
      setSports(await sportsRes.json());
      setMatches(await matchesRes.json());
      setStudents(await studentsRes.json());
      const lbRes = await fetch('/api/v1/sports/leaderboard', { headers: { 'Authorization': `Bearer ${token}` } });
      setLeaderboard(lbRes.ok ? await lbRes.json() : []);
    } catch {
      setError('Failed to load data');
    } finally {
      setLoading(false);
    }
  }, [token]);

  useEffect(() => { fetchData(); }, [fetchData]);

  const handleMatchSubmit = async (e) => {
    e.preventDefault();
    setError('');
    try {
      const response = await fetch('/api/v1/sports-matches', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', 'Authorization': `Bearer ${token}` },
        body: JSON.stringify({
          ...matchData, sport_id: parseInt(matchData.sport_id, 10),
          our_score: matchData.our_score ? parseInt(matchData.our_score, 10) : null,
          opponent_score: matchData.opponent_score ? parseInt(matchData.opponent_score, 10) : null,
          match_date: matchData.match_date ? matchData.match_date + ':00' : null
        })
      });
      if (response.ok) {
        setShowMatchForm(false);
        fetchData();
        setMatchData({ sport_id: '', opponent: '', match_date: '', venue: '', our_score: '', opponent_score: '', result: '', match_type: 'friendly', season: '', notes: '' });
      } else {
        setError(await safeErrorMessage(response, 'Failed to record match'));
      }
    } catch {
      setError('Network error. Please check your connection and try again.');
    }
  };

  const handleAchievementSubmit = async (e) => {
    e.preventDefault();
    setError('');
    try {
      const response = await fetch('/api/v1/sports-achievements', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', 'Authorization': `Bearer ${token}` },
        body: JSON.stringify({ ...achievementData, student_id: parseInt(achievementData.student_id, 10), sport_id: parseInt(achievementData.sport_id, 10) })
      });
      if (response.ok) {
        setShowAchievementForm(false);
        fetchData();
        setAchievementData({ student_id: '', sport_id: '', achievement_type: '', title: '', description: '', date_awarded: '', level: 'school' });
      } else {
        setError(await safeErrorMessage(response, 'Failed to record achievement'));
      }
    } catch {
      setError('Network error. Please check your connection and try again.');
    }
  };

  const resultBadge = (result) => {
    const map = { win: 'success', loss: 'danger', draw: 'warning' };
    return <Badge variant={map[result] || 'default'}>{result?.toUpperCase() || '—'}</Badge>;
  };

  if (loading) return <LoadingSkeleton type="text" rows={8} />;

  return (
    <div>
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '1rem', flexWrap: 'wrap', gap: '0.75rem' }}>
        <h2 style={{ fontSize: '1.25rem', fontWeight: 700, color: 'var(--color-text)' }}>Sports Management</h2>
        {canRecord && (
          <div style={{ display: 'flex', gap: '0.5rem' }}>
            <Button variant="primary" size="sm" onClick={() => { setShowMatchForm(!showMatchForm); setShowAchievementForm(false); setError(''); }}>
              <PlusIcon style={{ width: 16, height: 16 }} />
              {showMatchForm ? 'Cancel' : 'Record Match'}
            </Button>
            <Button variant="primary" size="sm" onClick={() => { setShowAchievementForm(!showAchievementForm); setShowMatchForm(false); setError(''); }}>
              <PlusIcon style={{ width: 16, height: 16 }} />
              {showAchievementForm ? 'Cancel' : 'Add Achievement'}
            </Button>
          </div>
        )}
      </div>

      {error && <div className="alert alert-error">{error}</div>}

      <Modal isOpen={showMatchForm} onClose={() => setShowMatchForm(false)} title="Record Match Result">
        <form onSubmit={handleMatchSubmit}>
          <div className="form-row">
            <div className="form-group">
              <label>Sport *</label>
              <select value={matchData.sport_id} onChange={(e) => setMatchData({...matchData, sport_id: e.target.value})} required>
                <option value="">Select Sport</option>
                {sports.map(s => <option key={s.id} value={s.id}>{s.name}</option>)}
              </select>
            </div>
            <div className="form-group">
              <label>Opponent *</label>
              <input type="text" value={matchData.opponent} onChange={(e) => setMatchData({...matchData, opponent: e.target.value})} required />
            </div>
          </div>
          <div className="form-row">
            <div className="form-group">
              <label>Match Date *</label>
              <input type="datetime-local" value={matchData.match_date} onChange={(e) => setMatchData({...matchData, match_date: e.target.value})} required />
            </div>
            <div className="form-group">
              <label>Venue</label>
              <input type="text" value={matchData.venue} onChange={(e) => setMatchData({...matchData, venue: e.target.value})} placeholder="Home/Away" />
            </div>
          </div>
          <div className="form-row">
            <div className="form-group">
              <label>Our Score</label>
              <input type="number" min="0" value={matchData.our_score} onChange={(e) => setMatchData({...matchData, our_score: e.target.value})} />
            </div>
            <div className="form-group">
              <label>Opponent Score</label>
              <input type="number" min="0" value={matchData.opponent_score} onChange={(e) => setMatchData({...matchData, opponent_score: e.target.value})} />
            </div>
          </div>
          <div className="form-row">
            <div className="form-group">
              <label>Result</label>
              <select value={matchData.result} onChange={(e) => setMatchData({...matchData, result: e.target.value})}>
                <option value="">Select</option>
                <option value="win">Win</option>
                <option value="loss">Loss</option>
                <option value="draw">Draw</option>
              </select>
            </div>
            <div className="form-group">
              <label>Match Type</label>
              <select value={matchData.match_type} onChange={(e) => setMatchData({...matchData, match_type: e.target.value})}>
                <option value="friendly">Friendly</option>
                <option value="league">League</option>
                <option value="tournament">Tournament</option>
                <option value="knockout">Knockout</option>
              </select>
            </div>
          </div>
          <div className="form-group">
            <label>Notes</label>
            <textarea value={matchData.notes} onChange={(e) => setMatchData({...matchData, notes: e.target.value})} rows="2" />
          </div>
          <div style={{ display: 'flex', gap: '0.5rem' }}>
            <Button type="submit" variant="primary">Record Match</Button>
            <Button type="button" variant="secondary" onClick={() => setShowMatchForm(false)}>Cancel</Button>
          </div>
        </form>
      </Modal>

      <Modal isOpen={showAchievementForm} onClose={() => setShowAchievementForm(false)} title="Add Achievement">
        <form onSubmit={handleAchievementSubmit}>
          <div className="form-row">
            <div className="form-group">
              <label>Student *</label>
              <select value={achievementData.student_id} onChange={(e) => setAchievementData({...achievementData, student_id: e.target.value})} required>
                <option value="">Select Student</option>
                {students.map(s => <option key={s.id} value={s.id}>{s.full_name}</option>)}
              </select>
            </div>
            <div className="form-group">
              <label>Sport *</label>
              <select value={achievementData.sport_id} onChange={(e) => setAchievementData({...achievementData, sport_id: e.target.value})} required>
                <option value="">Select Sport</option>
                {sports.map(s => <option key={s.id} value={s.id}>{s.name}</option>)}
              </select>
            </div>
          </div>
          <div className="form-row">
            <div className="form-group">
              <label>Title *</label>
              <input type="text" value={achievementData.title} onChange={(e) => setAchievementData({...achievementData, title: e.target.value})} required />
            </div>
            <div className="form-group">
              <label>Type</label>
              <select value={achievementData.achievement_type} onChange={(e) => setAchievementData({...achievementData, achievement_type: e.target.value})}>
                <option value="">Select</option>
                {achievementTypes.map(t => <option key={t} value={t}>{t.replace('_', ' ').toUpperCase()}</option>)}
              </select>
            </div>
          </div>
          <div className="form-row">
            <div className="form-group">
              <label>Date Awarded *</label>
              <input type="date" value={achievementData.date_awarded} onChange={(e) => setAchievementData({...achievementData, date_awarded: e.target.value})} required />
            </div>
            <div className="form-group">
              <label>Level</label>
              <select value={achievementData.level} onChange={(e) => setAchievementData({...achievementData, level: e.target.value})}>
                {levels.map(l => <option key={l} value={l}>{l.toUpperCase()}</option>)}
              </select>
            </div>
          </div>
          <div className="form-group">
            <label>Description</label>
            <textarea value={achievementData.description} onChange={(e) => setAchievementData({...achievementData, description: e.target.value})} rows="3" />
          </div>
          <div style={{ display: 'flex', gap: '0.5rem' }}>
            <Button type="submit" variant="primary">Add Achievement</Button>
            <Button type="button" variant="secondary" onClick={() => setShowAchievementForm(false)}>Cancel</Button>
          </div>
        </form>
      </Modal>

      <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(280px, 1fr))', gap: '1.5rem' }}>
        <Card title="Sports Leaderboard">
          {leaderboard.length === 0 ? (
            <p style={{ color: 'var(--color-text-muted)', textAlign: 'center', padding: '2rem' }}>No achievements recorded yet.</p>
          ) : (
            <Table columns={[
              { header: 'Rank', render: (_, idx) => `#${idx + 1}` },
              { header: 'Student', accessor: 'student_name' },
              { header: 'Achievements', render: (row) => <Badge variant="primary">{row.achievements_count}</Badge> },
            ]} data={leaderboard} />
          )}
        </Card>

        <Card title="Recent Matches">
          {matches.length === 0 ? (
            <p style={{ color: 'var(--color-text-muted)', textAlign: 'center', padding: '2rem' }}>No matches recorded.</p>
          ) : (
            <div style={{ maxHeight: '400px', overflowY: 'auto' }}>
              {matches.map(match => (
                <div key={match.id} style={{ padding: '0.75rem', borderBottom: '1px solid var(--color-border-light)' }}>
                  <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                    <strong>{match.sport}</strong>
                    {resultBadge(match.result)}
                  </div>
                  <div style={{ marginTop: '0.25rem', fontSize: '0.9rem' }}>vs <strong>{match.opponent}</strong></div>
                  <div style={{ fontSize: '0.85rem', color: 'var(--color-text-secondary)' }}>
                    Score: <strong>{match.our_score || '?'} - {match.opponent_score || '?'}</strong>
                  </div>
                  <div style={{ fontSize: '0.8rem', color: 'var(--color-text-muted)', marginTop: '0.25rem' }}>
                    {new Date(match.match_date).toLocaleDateString()}
                    {match.venue && ` at ${match.venue}`}
                  </div>
                </div>
              ))}
            </div>
          )}
        </Card>
      </div>
    </div>
  );
}

export default Sports;
